export const meta = {
  id: '47-word-order-questions-coordination',
  order: 47,
  titleFi: 'Sanajärjestys, kysymykset ja rinnastus',
  titleEn: 'Word order, questions and coordination',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: ['jȳdragon', 'skorion', 'drīvose', 'sindity', 'liorty', 'vaogēdy', 'qrīzahagon', 'boter', 'udrāzma', 'grevy', 'ours:brōzāzma', 'ours:botnon', 'ours:jitty', 'ours:āeksāzma', 'ours:representanti'],
  exampleIds: ['ex-d82f2878c6fb', 'ex-bec327f6bf2e', 'ex-31ccf215fc89', 'ex-d80c55316081', 'ex-dfaffec01443', 'ex-2e6e183a6fc9', 'hotd-s1e02-006', 'ex-0bc2cd55304f', 'ex-1d9a95c1415b', 'ex-729703340ff2', 'hotd-s2e04-007', 'ex-9902b5562bbe', 'ex-9d3e64c51631', 'ex-c454772797cc', 'ex-53dc6ef50613', 'ex-4cb89eba6378', 'ex-1cd23ccb2085', 'ex-2a8d86e9669b', 'ex-3e6e52d368c6', 'ex-6dab876dc463', 'ex-930f99fa9678', 'ex-f7d16b5b5b25', 'ex-991b56eca6b7'],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Pronouns',
    'https://wiki.languageinvention.com/index.php?title=User:Najahho/High_Valyrian_Verb_Moods',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes',
    'https://wiki.languageinvention.com/index.php?title=Skorverdon',
    'https://dedalvs.tumblr.com/post/175113985499/hi-i-have-a-question-about-high-valyrian-theres',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['word-order'],
  grammarAlso: ['questions', 'coordination'],
  showLines: ['hotd-s1e02-006', 'hotd-s2e04-007'],
  pronunciation: { focus: 'lengthening' },
  letter: {
    fi: 'Kirjoita hovin virallinen tervehdys: puhuttelu, arvonimet ja juhlava sanajärjestys.',
    en: 'Write a formal greeting at court: address, titles and a solemn word order.',
  },
  scene: 'hotd-s1e04-1',
}

# Sanajärjestys, kysymykset ja rinnastus

Duolingo esitteli asiat palasina: adjektiivin paikan, subjektipronominin poisjättämisen, yksinkertaisen rinnastuksen, kysymyssanat ja adverbien paikat. Tällä oppitunnilla palaset kootaan yhteen. Ensin käydään läpi koko lauseen järjestys, sitten painotus, kysymykset keskustelussa ja lopuksi se, miten valyriassa sanotaan *ja*. Siihen liittyy myös kysymys siitä, miltä virallinen kieli kuulostaa.

## Verbi loppuun

Wikin kielioppisivun mukaan valyria on vahvasti SOV-kieli: subjekti, objekti, verbi. Koko lauseen tavallinen järjestys on tämä:

1. ajan tai paikan ilmaus
2. subjekti
3. tapaa kuvaava adverbi
4. epäsuora objekti
5. suora objekti
6. muu adverbi
7. verbi

Daenerys noudattaa kaavaa kirjaimellisesti:

<Example id="ex-d82f2878c6fb" />

<Interlinear hv="Kesȳ tubī jemot dāervī tepan." gloss="tämä-LOC päivä-LOC te-DAT vapaus-ACC antaa-PRS.1SG" fi="Tänään annan teille vapauden." />

Ensin tulee aika, sitten epäsuora objekti ja suora objekti, ja verbi on viimeisenä. Wikin mukaan epäsuora objekti on yleensä suoran edellä (<Hv glossed>Taobot rūklon tepan</Hv> ’annan pojalle kukan’), mutta harvemmin myös toisin päin (<Hv glossed>Rūklon taobot tepan</Hv>).

Suomen sanajärjestys on vapaa, mutta verbi on yleensä lauseen toisena: *Tänään **annan** teille vapauden.* Valyriassa verbi tulee loppuun lähes aina. Esimerkeissä verbin jälkeen tulee yleensä vain puhuttelu tai jälkikäteen lisätty tarkennus (<Hv>Gevī ȳdrā, azantys!</Hv>).

## Painotus: pronomini mukaan

Koska verbi kertoo persoonan, subjektipronomini jätetään yleensä pois. Kun se otetaan mukaan, se painottaa subjektia. Wikin esimerkeissä painotus voimistuu askel kerrallaan:

- <Hv glossed>Gevī ȳdrā.</Hv> ’Puhut kauniisti.’
- <Hv glossed>Ao gevī ȳdrā.</Hv> ’**Sinä** puhut kauniisti.’
- <Hv glossed>Aōla gevī ȳdrā.</Hv> ’**Sinä itse** puhut kauniisti.’ (emfaattinen pronomini, <LessonLink n={37}>oppitunti 37</LessonLink>)

<Example id="ex-bec327f6bf2e" />

Daenerys esittäytyy painokkaasti, ja nuori Rhaenyra syyttää yhtä painokkaasti:

<Example id="ex-31ccf215fc89" />

<Example id="ex-d80c55316081" />

Suomessa tehdään samoin: *Minä olen Daenerys*, kun tavallisesti sanottaisiin vain *olen*.

## Kysymykset keskustelussa

**Kyllä/ei-kysymys** on sanajärjestykseltään sama kuin väite. Ero kuuluu vain sävelkulussa, joka nousee loppua kohti: <Hv glossed>Udrirzi Valyrio ȳdrā?</Hv> ’Puhutko valyriaa?’ Suomessa merkkinä on *-ko/-kö*, valyriassa pelkkä intonaatio.

**Kysymyssana** siirtyy lauseen alkuun. Se taipuu sen mukaan, mikä sen rooli lauseessa on. Kysymyksen voi siis rakentaa näin: ota väitelause, vaihda kysyttävä sana kysymyssanaksi ja siirrä se alkuun.

<Example id="ex-dfaffec01443" />

<Example id="ex-2e6e183a6fc9" />

<Hv glossed>Skorion</Hv> on abstrakti ’mikä’, ja <Hv glossed>jȳdragon</Hv> ’puhua jostakin’ -verbin kanssa aihe on akkusatiivissa. Kun kysymyssana on adjektiivi, se tuo substantiivinsa mukanaan alkuun:

<Line id="hotd-s1e02-006" />

Hyödyllinen kysymys keskustelussa koskee ikää. Valyriassa ei kysytä *kuinka vanha*, vaan *kuinka monta vuotta olet nähnyt*:

<Example id="ex-0bc2cd55304f" />

<Example id="ex-1d9a95c1415b" />

Duolingosta muistat, että <Hv>skorverdon</Hv> on itse lauseenjäsen, ja sen perässä on monikon genetiivi. Tässä tilalla on kollektiivin genetiivi <Hv l="jēdar-2">jēdaro</Hv>, koska *vuosi* on valyriaksi kollektiivi <Hv l="jēdar-2">jēdar</Hv>.

**Häntäkysymys** (*eikö niin?*) tehdään kieltosanalla. Hizdahr käyttää muotoa <Hv glossed>kesos daor</Hv>, nuori Rhaenyra pelkkää sanaa <Hv>daor</Hv>:

<Example id="ex-729703340ff2" />

<Line id="hotd-s2e04-007" />

Wiki huomauttaa lisäksi, että kielteinen kysymys voi olla merkitykseltään myönteinen, aivan kuten suomen kohtelias *Ettekö halua kahvia?* on tarjous eikä epäily. Petersonin esimerkki on <Hv glossed>Nuiōrkī sōnari raqō daor?</Hv> ’Nautitko talvesta New Yorkissa?’

## ”Ja” ilman ja-sanaa: konjunktiivinen pidennys

Duolingo mainitsi tämän ohimennen: kun rinnastetaan kaksi tai useampia paljaita sanoja, valyria ei käytä sanaa <Hv glossed>se</Hv> ’ja’. Sen sijaan **viimeistä sanaa muutetaan**: sen loppuvokaali pitenee ja paino siirtyy sille. Wikissä on ääntämysesimerkki: <Hv glossed>vala ābra</Hv> [ˈvala ˈaːbra] tarkoittaa ’mies, nainen’, ja <Hv glossed>vala ābrā</Hv> [ˈvala aːˈbraː] tarkoittaa ’mies ja nainen’.

<Note>
Suomalaiselle tämä on kaksinkertainen ansa. Suomessa paino on aina ensitavulla, eikä pitkä vokaali vedä painoa puoleensa. Valyrian rinnastuksessa paino hyppää sanan loppuun. Lisäksi lauseen sanoja ei voi tulkita yksittäin, koska <Hv k="and">ābrā</Hv> on myös sanan *nainen* lokatiivi. Vasta lause kertoo, kumpi on kyseessä.
</Note>

Wikin kielioppisivulla säännöt on koottu sen mukaan, miten sana päättyy:

- **Lyhyt vokaali pitenee**: <Hv glossed>vala rōbīr</Hv> ’mies ja viikuna’, <Hv glossed>rōbir valā</Hv> ’viikuna ja mies’.
- **Diftongin ensimmäinen osa pitenee** (ae, ao): <Hv glossed>vala jāos</Hv> ’mies ja koira’, <Hv glossed>vala āo</Hv> ’mies ja sinä’.
- **Diftongin toinen osa pitenee** (ia, io, ua…): <Hv glossed>vala Valyriā</Hv> ’mies ja Valyria’.
- **Pitkän vokaalin (paitsi ā) perään tulee -si tai -sy**: <Hv glossed>Jenti voktīsi ūndan</Hv> ’näin johtajan ja papit’. Jos sana päättyy konsonanttiin, perään tulee pelkkä -i tai -y: <Hv glossed>Valot aōty rūkla teptan</Hv> ’annoin kukkia miehelle ja sinulle’.
- **Pitkä ā muuttuu diftongiksi āe**: <Hv glossed>Valā riñāe glaestan</Hv> ’asuin miehen ja tytön lähellä’.

Luettelossa vain viimeinen sana muuttuu. Melisandre kuvaa menneisyyttään pidennyksellä, kun taas punainen papitar sanoo saman asian sanalla <Hv l="se">se</Hv>:

<Example id="ex-9902b5562bbe" />

<Example id="ex-9d3e64c51631" />

<Hv glossed>Sindita liortā</Hv> on ’ostettu ja myyty’. Perfektin partisiippi <Hv>liorta</Hv> (<LessonLink n={40}>oppitunti 40</LessonLink>) on pidentynyt muotoon <Hv fi="ja myyty">liortā</Hv>, joka sattuu näyttämään täsmälleen verbin perfektin 2. persoonalta. Kolmen sanan luetteloita:

<Example id="ex-c454772797cc" />

<Example id="ex-53dc6ef50613" />

<Example id="ex-4cb89eba6378" />

Viimeisessä esimerkissä <Hv>sōluttī</Hv> päättyy pitkään vokaaliin, joten pidennys tehdään lisäämällä -si: <Hv fi="ja kääröt">sōluttīsi</Hv>. Myös verbejä voi rinnastaa näin. Areenan taistelijat huutavat:

<Example id="ex-1cd23ccb2085" />

<Interlinear hv="Aōhor jaqiarzir ivīlībin imorghūlīn, jaqiarzus Dārȳs." gloss="sinun-AQ.ACC kunnia-ACC taistella.puolesta-AOR.1SG kuolla.puolesta-AOR.1SG=ja kunniakas-LUN.VOC kuningatar-VOC" fi="Taistelen ja kuolen kunniasi puolesta, oi kunniakas kuningatar." />

Molemmat verbit ovat obliikvisen applikatiivin muotoja (etuliite i-, <LessonLink n={33}>oppitunti 33</LessonLink>), joten se, minkä puolesta taistellaan, on akkusatiivissa. Pidennys näkyy vain jälkimmäisessä verbissä: <Hv>imorghūlin</Hv> muuttuu muotoon <Hv>imorghūlīn</Hv>.

## Milloin sitten käytetään se-sanaa?

Petersonin mukaan <Hv l="se">se</Hv> ei ole kahden substantiivin välissä virhe vaan ainoastaan tarpeeton ja hieman epätavallinen (wikin kieliopin mukaan se on harvinainen ja muodollinen). Kun sanoja on kolme tai enemmän, <Hv>se</Hv> on tavallisempi, ja Duolingon kurssi käyttää sitä silloin aina: <Hv fi="Korpit, haukat ja pöllöt ovat lintuja.">Vōljesse, ūlia, se atroksī hontesse issi.</Hv> Se on myös tavallinen ratkaisu silloin, kun pidennystä ei voi käyttää:

- **määritteiden välissä**: <Hv glossed>bāne se adere</Hv> ’kuuma ja nopea’ (kielioppisivulla kirjoitettu ilman pituusmerkkiä)
- **pidempien ilmausten ja lauseiden välissä**: <Hv glossed>dohaeragon se morghūljagon</Hv> ’palvella ja kuolla’

<Example id="ex-3e6e52d368c6" />

Esimerkissä rinnastetaan kokonaiset ilmaukset (*hyvä mies* ja *hieno ritari*), joten tarvitaan <Hv l="se">se</Hv>. Nuori Rhaenyra käyttää sanaa <Hv l="se">se</Hv> myös verbien välissä, ja syytös kuulostaa painokkaalta:

<Example id="ex-2a8d86e9669b" />

**Yhdistetyssä rinnastuksessa** käytetään molempia keinoja, sekä sanaa <Hv l="se">se</Hv> että pidennystä. Merkitys on ’sekä… että’. Wikin esimerkki on <Hv glossed>Vala se ābrā sȳrī vāedis</Hv> ’sekä mies että nainen laulavat hyvin’. Daenerys latoo <Hv l="se">se</Hv>-sanoja peräkkäin retorisena keinona:

<Example id="ex-6dab876dc463" />

## Rekisteri: kun haluaa kuulostaa viralliselta

Juhlavaa sävyä voi luoda määräävän sanan paikalla. Lisäksi kannattaa katsoa, miten pitkä luettelo rakentuu.

**Adjektiivi pääsanan jälkeen.** Petersonin mukaan merkitys ei muutu: <Hv fi="iloinen nainen">Kirine ābra</Hv> ja <Hv fi="iloinen nainen">Ābra kirine</Hv> tarkoittavat samaa. Wiki lainaa Petersonia: kaikki adjektiivit voivat olla jälkiasemassa, mutta <Term id="maarite">määräävät sanat</Term> (kuten demonstratiivit) ovat yleensä edellä, ”ellet halua kuulostaa… viralliselta”. Omistusadjektiivi on kuitenkin melko usein jälkiasemassa, erityisesti puhuttelussa:

<Example id="ex-f7d16b5b5b25" />

<Example id="ex-991b56eca6b7" />

**<Hv l="se">Se</Hv> luetteloissa.** Missandein titteliluettelossa käytetään sanaa <Hv l="se">se</Hv> eikä pidennystä. Sana ei kuitenkaan sinänsä tee luettelosta juhlavaa, sillä Petersonin mukaan se on kolmen tai useamman sanan luettelossa tavallinen:

<Example id="ex-930f99fa9678" />

## Kokoa itse

<Practice hv="Vala ābrā udrirzi Valyrio ȳdrasi." fi="Mies ja nainen puhuvat valyriaa." />

<Practice hv="Nyke udrirzi Valyrio ȳdran." fi="Minä puhun valyriaa." />

<Practice hv="Kesy sȳz issa, kesos daor?" fi="Tämä on hyvä, eikö olekin?" />

<Practice hv="Skorverdon jēdaro ūndā?" fi="Kuinka vanha olet? (Kuinka monta vuotta olet nähnyt?)" />

<Practice hv="Hārēpsa jēdari ūndan." fi="Olen kolmekymmentä. (Olen nähnyt kolmekymmentä vuotta.)" />

<Vocab />

<Check />
